import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import { action, computed } from '@ember/object';
import { equal, filterBy, reads } from '@ember/object/computed';
import { USER_ROLE_ADMIN, USER_ROLE_MANAGER } from 'cio/utils/user-roles';

import AuthService from 'cio/services/auth';
import AccountService from 'cio/services/account';
import UserModel from 'cio/models/user';
import AnalyticsService from 'cio/services/analytics';
import SsoService from 'cio/services/sso';
import EnvironmentModel from 'cio/models/environment';
import DS from 'ember-data';
import { isEmpty } from '@ember/utils';
import { sortByCaseInsensitive } from 'cio/utils/sort';

export default class SettingsTeamIndexController extends Controller {
  @service auth!: AuthService;
  @service alerts: any;
  @service account!: AccountService;
  @service analytics!: AnalyticsService;
  @service sso!: SsoService;
  @service store!: DS.Store;

  declare model: { environments: EnvironmentModel[]; users: UserModel[] };

  _isCreating = false;

  setDefaults() {
    this.set('_isCreating', false);
  }

  @reads('model.environments') environments!: EnvironmentModel[];

  newUser!: UserModel;

  @filterBy('model.users', 'role', USER_ROLE_ADMIN) usersAdmin!: UserModel[];
  @equal('usersAdmin.length', 1) hasOnlyOneAccountAdmin!: boolean;

  @computed('model.users.@each.{role,lastName,firstName}')
  get sortedUsers() {
    return sortByCaseInsensitive(this.model.users, ['lastName', 'firstName', 'role']);
  }

  @computed('environments.@each.name')
  get sortedEnvironments() {
    return sortByCaseInsensitive(this.environments, ['name']);
  }

  @computed('model.users.@each.emailDomain', 'sso.ssoConfiguration.identityProvider')
  get hasTeamMembersWithGoogleSSODomainMismatch() {
    if (this.sso.ssoConfiguration == null || isEmpty(this.sso.ssoConfiguration.oidcGoogleHostedDomain)) {
      return false;
    }

    return this.model.users.toArray().some((user: UserModel) => {
      return user.emailDomain !== this.sso.ssoConfiguration?.oidcGoogleHostedDomain;
    });
  }

  @action
  startCreate() {
    this.set(
      'newUser',
      this.store.createRecord('user', {
        role: USER_ROLE_MANAGER,
        accessibleEnvironments: this.environments,

        // "create" endpoint in Unity doesn't return 2FA status
        // setting it to false here avoids need to refresh model after POST request
        twoFactorAuthenticationEnabled: false,
      }),
    );
    this.set('_isCreating', true);
    this.analytics.track('user', 'invite_started');
  }

  @action
  cancelCreate() {
    this.set('newUser', null);
    this.set('_isCreating', false);
  }

  @action
  resetPassword(user: UserModel) {
    return user.resetPassword({}).then(() => {
      this.alerts.success(
        'user.passwordReset.success',
        "Password reset directions are being sent to the user's email address.",
      );
    });
  }

  @action
  updateUser(user: UserModel) {
    let removingCurrentUserAdmin = user.role !== USER_ROLE_ADMIN && user.id === this.auth.user?.id;

    return user.save().then(() => {
      this.alerts.success('user.update.success', 'User data updated');

      // current user is no longer an admin:
      // we need to refresh all user records, which API will return less data for
      // (currently: 2FA status of other users is visible only to admins; hidden otherwise)
      if (removingCurrentUserAdmin) {
        this.send('refreshModel');
      }
    });
  }

  @action
  async createUser(user: UserModel) {
    try {
      await user.save();
      this.alerts.success('user.new.success', 'New team member invited');
      this.set('_isCreating', false);
      return this.send('refreshModel');
    } catch (e) {
      if (e.errors?.[0]?.status === '422' || e.errors?.[0]?.source != null) {
        let source = e.errors?.[0]?.source.pointer;

        if (source === '/data/attributes/user') {
          this.alerts.error('user.new.error', 'User with this email already exists.');
        } else {
          this.alerts.error('user.new.error', 'Error inviting new team member.');
        }
      } else {
        this.alerts.error('user.new.error', 'Error inviting new team member.');
      }
    }
  }

  @action
  deleteUser(user: UserModel) {
    return user.destroyRecord().then(() => {
      this.alerts.success('user.delete.success', 'User deleted');
    });
  }
}
